import { SporsmalType } from '../../types/generated-types';
import { Kundeinfo } from '../../types/internal-types';
import { formatKontonummer } from '../format';
import ID from '../id';
import { BesvartSporsmal, CurrentSporsmal } from '../reducer/current-steg';
import { IdentifikatorType, Kontaktinfosporsmal } from '../reducer/sporsmalsflyt-types';
import { validateFodselsnummer } from '../validation/validate';
import { Kontaktinfofelt } from './kontaktinfofelt';

export interface CurrentKontaktinfosporsmal {
    type: SporsmalType.Kontaktinfo;
    sporsmal: Kontaktinfosporsmal;
    navn: string;
    mobilnummer: string;
    epost: string;
    fodselsnummer: string;
    organisasjonsnummer: string;
    identifikatorType: IdentifikatorType;
    bankkontonummer: string;
    showFodselsnummer: boolean;
}

export type BesvartKontaktinfosporsmal = CurrentKontaktinfosporsmal;

export const isKontaktinfoSporsmal = (
    currentSporsmal: CurrentSporsmal,
): currentSporsmal is CurrentKontaktinfosporsmal => currentSporsmal.type === SporsmalType.Kontaktinfo;

const kundenEierForsikringen = (besvarteSporsmal: BesvartSporsmal[]): boolean => {
    const forsikringEierSporsmal = besvarteSporsmal.find(
        (besvartSporsmal) => besvartSporsmal.sporsmal.id === ID.SPORSMAL.FORSIKRINGS_EIER,
    );

    if (forsikringEierSporsmal && forsikringEierSporsmal.type === SporsmalType.Radio) {
        return forsikringEierSporsmal.svarId === ID.SVAR.JA_NEI_SPORSMAL_SVAR_JA;
    }

    return true;
};

export const initializeCurrentKontaktinfosporsmal = (
    sporsmal: Kontaktinfosporsmal,
    kundeinfo: Kundeinfo,
    identifikatorType: IdentifikatorType,
    besvarteSporsmal: BesvartSporsmal[],
): CurrentKontaktinfosporsmal => {
    if (kundeinfo && kundenEierForsikringen(besvarteSporsmal)) {
        return {
            type: SporsmalType.Kontaktinfo,
            sporsmal,
            navn: kundeinfo.fornavn
                ? `${kundeinfo.fornavn} ${kundeinfo.etternavn || ''}`
                : `${kundeinfo.etternavn || ''}`,
            mobilnummer: kundeinfo.mobilnummer,
            epost: kundeinfo.epost,
            fodselsnummer: identifikatorType === IdentifikatorType.Fodselsnummer ? kundeinfo.offentligId : '',
            organisasjonsnummer:
                identifikatorType === IdentifikatorType.Organisasjonsnummer ? kundeinfo.offentligId : '',
            identifikatorType,
            showFodselsnummer:
                identifikatorType === IdentifikatorType.Fodselsnummer && !validateFodselsnummer(kundeinfo.offentligId),
            bankkontonummer: '',
        };
    }
    return {
        type: SporsmalType.Kontaktinfo,
        sporsmal,
        navn: '',
        mobilnummer: '',
        epost: '',
        fodselsnummer: '',
        organisasjonsnummer: '',
        identifikatorType,
        showFodselsnummer: identifikatorType === IdentifikatorType.Fodselsnummer,
        bankkontonummer: '',
    };
};

const updateKontaktinfofelt = (
    opprinneligKontaktinfosvar: CurrentKontaktinfosporsmal,
    felt: Kontaktinfofelt,
    svarTekst: string,
): CurrentKontaktinfosporsmal => {
    switch (felt) {
        case Kontaktinfofelt.Navn:
            return { ...opprinneligKontaktinfosvar, navn: svarTekst };
        case Kontaktinfofelt.Mobilnummer:
            return { ...opprinneligKontaktinfosvar, mobilnummer: svarTekst.trim() };
        case Kontaktinfofelt.Epost:
            return { ...opprinneligKontaktinfosvar, epost: svarTekst };
        case Kontaktinfofelt.Fodselsnummer:
            return { ...opprinneligKontaktinfosvar, fodselsnummer: svarTekst.trim() };
        case Kontaktinfofelt.Organisasjonsnummer:
            return { ...opprinneligKontaktinfosvar, organisasjonsnummer: svarTekst.trim() };
        case Kontaktinfofelt.Bankkontonummer:
            return { ...opprinneligKontaktinfosvar, bankkontonummer: formatKontonummer(svarTekst) };
        default:
            return opprinneligKontaktinfosvar;
    }
};

export const updateCurrentKontaktinfo = (
    currentSporsmal: CurrentSporsmal,
    felt: Kontaktinfofelt,
    svarTekst: string,
): CurrentSporsmal => {
    if (isKontaktinfoSporsmal(currentSporsmal)) {
        return updateKontaktinfofelt(currentSporsmal, felt, svarTekst);
    }
    return currentSporsmal;
};
